import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {METHOD_KEYS} from '~/constants/medical-questionnaires';
import {useNotification} from '~/hooks';
import EmergencyContraception from '~/pages/homes/emergency-contraception';
import SexualHealth from '~/pages/homes/sexual-health';
import STITesting from '~/pages/homes/sti-testing';
import STITeleconsultation from '~/pages/homes/sti-teleconsultation';
import MenstrualHealth from '~/pages/homes/menstrual-health';
import {fetchQuestionnaires} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import NotFoundPage from '../not-found';
import BirthControlRequest from '~/pages/homes/birth-control-request';
import BirthControlTeleconsultation from '~/pages/homes/birth-control-teleconsultation';
import BirthControlRefill from '~/pages/homes/birth-control-refill';
import HairLossTeleconsultation from '~/pages/homes/hair-loss-teleconsultation';
import CheckoutPopup from '~/pages/homes/pop-up/CheckoutPopup';
import AcneTeleconsultation from '~/pages/homes/acne-teleconsultation';
import WeightLoss from '~/pages/homes/weight-loss';
import MenopauseTeleconsultation from '~/pages/homes/menopause-teleconsultation';
import FertilityTest from '~/pages/homes/fertility-test';
import MenopausalProfileTest from '~/pages/homes/menopausal-profile-test';
import BoneDensityTest from '~/pages/homes/bone-density-test';
import HotFlashesTreatment from '~/pages/homes/hot-flashes-treatment';
import HPVTest from '~/pages/homes/hpv-test';
import HPVVaccination from '~/pages/homes/hpv-vaccination';
import PapSmearTest from '~/pages/homes/pap-smear-test';
import VaginalDrynessTreatment from '~/pages/homes/vaginal-dryness-treatment';
import PCOSTest from '~/pages/homes/pcos-test';
import MammogramBreastUltrasoundTest from '~/pages/homes/mammogram-breast-ultrasound-test';
import ThyroidFunctionTest from '~/pages/homes/thyroid-function-test';
import AmenorrhoeaTest from '~/pages/homes/amenorrhoea-test';
import PeriodControlMedication from '~/pages/homes/period-control-medication';
import {getCurrentUserFromStorage} from '~/services/auth';
import {useHistory} from 'react-router-dom';
import {setRebookingAppointment} from '~/redux/questionnaires/slice';
const queryString = require('query-string');
import moment from 'moment';
import * as storage from '~/utils/storage';

const HomesPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const methodKey = useSelector((state: RootState) => state.questionnaires.methodKey);
  const query: {values: string | undefined} = queryString.parse(location.search);
  const {values} = query;
  const [userQuestionId, orderId, type, key, time] = values ? values.split('/') : [];
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const history = useHistory();

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(
        fetchQuestionnaires({methodKey: methodKey === 'REBOOKING' ? METHOD_KEYS.BIRTH_CONTROL_REQUEST : methodKey}),
      );
    };
    fetchData();
  }, [dispatch, methodKey]);

  if (orderId && userQuestionId) {
    const alreadyBookedAppointment = storage.load(`${time}`);
    let isExpire;
    if (time) {
      const now = moment().format('YYYY-MM-DD HH:mm:ss');
      const dateObj = moment.unix(+time).add(7, 'days');
      const expiredDateFormat = dateObj.format('YYYY-MM-DD HH:mm:ss');
      isExpire = moment(expiredDateFormat).isBefore(now);
    }
    if (isExpire) {
      if (alreadyBookedAppointment) {
        storage.remove(`${time}`);
      }
      history.push(`/expired-booking-appointment`);
    } else {
      dispatch(setRebookingAppointment({orderId, userQuestionId, typeAppt: type, key, time}));
      history.push(`/appointment?type=${type}&methodKey=${methodKey}&token=${savedToken}`);
    }
  }

  const renderMedicalQuestionnaires = () => {
    if (orderId || methodKey === 'REBOOKING') {
      return <NotFoundPage />;
    }
    switch (methodKey) {
      case METHOD_KEYS.EMERGENCY_CONTRACEPTION:
        return <EmergencyContraception />;
      case METHOD_KEYS.SEXUAL_HEALTH:
        return <SexualHealth />;
      case METHOD_KEYS.BIRTH_CONTROL_REQUEST:
        return <BirthControlRequest />;
      case METHOD_KEYS.STI_TESTING:
        return <STITesting />;
      case METHOD_KEYS.STI_TELECONSULTATION:
        return <STITeleconsultation />;
      case METHOD_KEYS.MENSTRUAL_HEALTH:
        return <MenstrualHealth />;
      case METHOD_KEYS.BIRTH_CONTROL_TELECONSULTATION:
        return <BirthControlTeleconsultation />;
      case METHOD_KEYS.BIRTH_CONTROL_REFILL:
        return <BirthControlRefill />;
      case METHOD_KEYS.HAIR_LOSS_TELECONSULTATION:
        return <HairLossTeleconsultation />;
      case METHOD_KEYS.PH_CHECKOUT:
        return <CheckoutPopup />;
      case METHOD_KEYS.ACNE_TELECONSULTATION:
        return <AcneTeleconsultation />;
      case METHOD_KEYS.WEIGHT_LOSS_PROGRAM:
        return <WeightLoss />;
      case METHOD_KEYS.MENOPAUSE_TELECONSULTATION:
        return <MenopauseTeleconsultation />;
      case METHOD_KEYS.FERTILITY_TEST:
        return <FertilityTest />;
      case METHOD_KEYS.MENOPAUSAL_PROFILE_TEST:
        return <MenopausalProfileTest />;
      case METHOD_KEYS.BONE_DENSITY_TEST:
        return <BoneDensityTest />;
      case METHOD_KEYS.HOT_FLASHES_TREATMENT:
        return <HotFlashesTreatment />;
      case METHOD_KEYS.HPV_TEST:
        return <HPVTest />;
      case METHOD_KEYS.HPV_VACCINATION:
        return <HPVVaccination />;
      case METHOD_KEYS.PAP_SMEAR_TEST:
        return <PapSmearTest />;
      case METHOD_KEYS.VAGINAL_DRYNESS_TREATMENT:
        return <VaginalDrynessTreatment />;
      case METHOD_KEYS.PCOS_TEST:
        return <PCOSTest />;
      case METHOD_KEYS.MAMMOGRAM_BREAST_ULTRASOUND_TEST:
        return <MammogramBreastUltrasoundTest />;
      case METHOD_KEYS.THYROID_FUNCTION_TEST:
        return <ThyroidFunctionTest />;
      case METHOD_KEYS.AMENORRHOEA_TEST:
        return <AmenorrhoeaTest />;
      case METHOD_KEYS.PERIOD_CONTROL_MEDICATION:
        return <PeriodControlMedication />;
      default:
        return <NotFoundPage />;
    }
  };

  return renderMedicalQuestionnaires();
};

export default HomesPage;
